import { useState, useEffect, useRef } from 'react';
import {
  Clock, Bell, Settings as Cog, Webhook, Satellite, CreditCard, Database, BarChart3,
  MessageSquare, FileSpreadsheet, Landmark, Users, Banknote, Play, RotateCcw, ArrowRight,
  ShieldCheck, ShieldAlert,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { BillingOverview, BillingAccount, BillingSettings } from '../types';

type Stat = 'done' | 'pend' | 'gate' | 'ext';
type Col = 'Triggers' | 'Edge functions' | 'Core' | 'Outputs';

interface NodeDef {
  id: string; label: string; sub: string; col: Col; status: Stat;
  icon: React.ComponentType<{ className?: string }>;
}

const NODES: NodeDef[] = [
  { id: 'cron', label: 'Vercel Cron', sub: 'Daily 14:00 UTC', col: 'Triggers', status: 'pend', icon: Clock },
  { id: 'webhook', label: 'Stripe Webhook', sub: 'Real-time events', col: 'Triggers', status: 'pend', icon: Bell },
  { id: 'billingcron', label: 'billing-cron', sub: 'The engine · 6 steps', col: 'Edge functions', status: 'pend', icon: Cog },
  { id: 'webhookfn', label: 'stripe-webhook', sub: 'Verify → sync → alert', col: 'Edge functions', status: 'pend', icon: Webhook },
  { id: 'apifn', label: 'api / billing', sub: 'Read + config routes', col: 'Edge functions', status: 'done', icon: Satellite },
  { id: 'stripe', label: 'Stripe', sub: 'Invoices · payments', col: 'Core', status: 'ext', icon: CreditCard },
  { id: 'db', label: 'XYZ-HQ DB', sub: '6 billing tables', col: 'Core', status: 'done', icon: Database },
  { id: 'dash', label: 'Billing Dashboard', sub: 'AR · cash · invoices', col: 'Outputs', status: 'done', icon: BarChart3 },
  { id: 'slack', label: 'Slack Digest', sub: 'AR · overdue · cash', col: 'Outputs', status: 'pend', icon: MessageSquare },
  { id: 'qb', label: 'QuickBooks CSV', sub: 'Manual reconcile', col: 'Outputs', status: 'done', icon: FileSpreadsheet },
];

const EXTRA: NodeDef[] = [
  { id: 'banking', label: 'Banking — Relay + Chase', sub: 'Read-only · feeds cash', col: 'Core', status: 'ext', icon: Landmark },
  { id: 'clients', label: 'Clients', sub: 'Mapped to Stripe by ID', col: 'Core', status: 'done', icon: Users },
  { id: 'money', label: 'Money Movement', sub: 'Recommend-only', col: 'Outputs', status: 'gate', icon: Banknote },
];

const COLS: Col[] = ['Triggers', 'Edge functions', 'Core', 'Outputs'];

const STATUS_LABEL: Record<Stat, string> = {
  done: 'Built & live', pend: 'Needs secrets / deploy', gate: 'Go-live gate', ext: 'External service',
};
const STATUS_CLASS: Record<Stat, string> = {
  done: 'text-success border-success/40 bg-success/10',
  pend: 'text-warning border-warning/40 bg-warning/10',
  gate: 'text-destructive border-destructive/40 bg-destructive/10',
  ext: 'text-info border-info/40 bg-info/10',
};

// daily run steps: which nodes light up
const STEPS = [
  { t: 'Reconcile', d: 'Pull Stripe invoices → HQ invoices mirror.', nodes: ['stripe', 'db'] },
  { t: 'Snapshot cash', d: 'Record Stripe + bank balances → cash_snapshots.', nodes: ['stripe', 'banking', 'db'] },
  { t: 'Generate invoices', d: 'For clients due today.', nodes: ['db', 'stripe'], safe: 'Created as DRAFT only — nothing emailed.', live: 'Finalized & emailed (ACH + card).' },
  { t: 'Recommend money moves', d: 'Tax reserve · low-balance · payout funding.', nodes: ['db', 'money'] },
  { t: 'Dunning', d: 'Flag overdue invoices.', nodes: ['db', 'slack'], safe: 'Internal flag only.', live: 'Stripe reminders + Slack escalation.' },
  { t: 'Notify', d: 'Post the run digest to Slack.', nodes: ['slack'] },
];

function money(n: number | null | undefined): string {
  return (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function BillingFlow({ overview, accounts, settings }: {
  overview: BillingOverview | null;
  accounts: BillingAccount[];
  settings: BillingSettings | null;
}) {
  const realLive = !!settings?.billing_live && !settings?.kill_switch;
  const [preview, setPreview] = useState<'safe' | 'live'>(realLive ? 'live' : 'safe');
  const [selected, setSelected] = useState<string>('billingcron');
  const [step, setStep] = useState<number>(-1);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  const play = () => {
    if (timer.current) clearInterval(timer.current);
    let i = 0;
    setStep(0);
    timer.current = setInterval(() => {
      i += 1;
      if (i >= STEPS.length) { clearInterval(timer.current!); timer.current = null; setStep(STEPS.length - 1); return; }
      setStep(i);
    }, 1100);
  };
  const reset = () => { if (timer.current) clearInterval(timer.current); timer.current = null; setStep(-1); };

  const activeNodes = step >= 0 ? STEPS[step].nodes : [];
  const isLive = preview === 'live';

  // live figures
  const aging = overview?.aging;
  const overdue30 = (aging?.['31-60'] || 0) + (aging?.['61-90'] || 0) + (aging?.['90+'] || 0);
  const linked = accounts.filter((a) => a.stripe_customer_id).length;
  const recs = overview?.recommendations.length ?? 0;

  const detailFor = (id: string): { title: string; body: string; points: string[] } => {
    const m: Record<string, { title: string; body: string; points: string[] }> = {
      cron: { title: 'Vercel Cron', body: 'Fires daily and calls /api/billing-tick, which proxies to the billing-cron edge function (same pattern as the Hermes job).', points: ['Schedule committed in vercel.json (14:00 UTC)', 'Needs Vercel env at go-live'] },
      webhook: { title: 'Stripe Webhook', body: 'Stripe pushes invoice & payout events instantly — HQ had no real-time payment status before.', points: ['Signature-verified', 'Register endpoint at go-live'] },
      billingcron: { title: 'billing-cron (engine)', body: 'Each run: reconcile → snapshot → invoice → recommend → dunning → notify. SAFE-gated end to end.', points: ['Built & committed', 'Deploy needs STRIPE_SECRET_KEY + BILLING_CRON_SECRET'] },
      webhookfn: { title: 'stripe-webhook fn', body: 'Verifies the signature, upserts the invoices mirror, appends an audit event, Slacks on paid/failed.', points: ['Built & committed', 'Deploy with verify_jwt = false'] },
      apifn: { title: 'api / billing routes', body: 'Admin-gated read & config endpoints powering this dashboard.', points: ['DEPLOYED — live now', 'No secrets needed (read side)'] },
      stripe: { title: 'Stripe', body: 'System of record for invoices & payments. Email invoice + ACH/card; idempotency keys on every write.', points: [`${overview ? money(aging?.total) : '—'} open AR tracked`, 'Legacy auto-charge subs replaced'] },
      db: { title: 'XYZ-HQ Database', body: 'Six Supabase tables (client_billing, invoices, billing_events, cash_snapshots, money_moves, billing_settings). RLS = admin/owner.', points: ['LIVE — migration applied', `${linked} clients mapped to Stripe by ID`] },
      dash: { title: 'Billing Dashboard', body: 'This view — MRR, AR aging, cash, recommendations, invoices, config, CSV export.', points: ['Built & deployed'] },
      slack: { title: 'Slack Digest', body: 'Server-side webhook posts the daily AR/overdue/cash digest + real-time alerts.', points: ['Needs BILLING_SLACK_WEBHOOK_URL', 'System webhook — respects the always-draft rule'] },
      qb: { title: 'QuickBooks CSV', body: 'One-click export of invoices + payments for manual QBO reconciliation (no QuickBooks API available).', points: ['Built — GET /billing/export.csv'] },
      banking: { title: 'Banking — Relay + Chase', body: 'XYZ operating cash. Read-only via connected tools, so balances feed the dashboard and recommendations.', points: ['No write API → transfers recommend-only'] },
      clients: { title: 'Clients', body: 'Billing clients linked to Stripe customers by ID (emails did not match — the original root cause).', points: [`${accounts.length} configured · ${linked} linked`, 'Encore paused · SISU churned'] },
      money: { title: 'Money Movement', body: 'Computes tax reserve, low-balance top-ups and payout funding, then recommends them. Execution stays manual.', points: [`${recs} open recommendation${recs === 1 ? '' : 's'}`, 'Recommend-only by necessity'] },
    };
    return m[id] ?? { title: id, body: '', points: [] };
  };

  const d = detailFor(selected);

  const NodeCard = (n: NodeDef) => {
    const Icon = n.icon;
    const sel = selected === n.id;
    const flow = activeNodes.includes(n.id);
    return (
      <button
        key={n.id}
        onClick={() => setSelected(n.id)}
        className={`text-left w-full rounded-md border p-3 transition
          ${sel ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/60'}
          ${flow ? 'border-[var(--color-gold)] ring-1 ring-[var(--color-gold)] shadow-[0_0_24px_rgba(184,150,62,.35)]' : ''}`}
      >
        <div className="flex items-center gap-2 font-medium text-sm">
          <Icon className="h-4 w-4 shrink-0" />
          <span>{n.label}</span>
          {n.id === 'money' && <Banknote className="hidden" />}
        </div>
        <div className="text-xs text-muted-foreground mt-1">{n.sub}</div>
        <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border ${STATUS_CLASS[n.status]}`}>
          {STATUS_LABEL[n.status]}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {/* mode banner + preview toggle */}
      <div className={`rounded-md border p-3 flex flex-wrap items-center gap-3 text-sm
        ${realLive ? 'border-success/30 bg-success/10 text-success' : 'border-warning/30 bg-warning/10 text-warning'}`}>
        {realLive ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
        <span className="font-semibold">{realLive ? 'LIVE' : 'SAFE MODE'}</span>
        <span>{realLive ? 'Invoices auto-send on each billing day.' : 'No client emails send — invoices stay drafts until go-live.'}</span>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Preview:</span>
          <Button size="sm" variant={preview === 'safe' ? 'default' : 'outline'} className="h-7 px-2" onClick={() => setPreview('safe')}>SAFE</Button>
          <Button size="sm" variant={preview === 'live' ? 'default' : 'outline'} className="h-7 px-2" onClick={() => setPreview('live')}>LIVE</Button>
        </div>
      </div>

      {/* KPI strip (live) */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <Kpi label="Active MRR" value={money(overview?.mrr)} />
        <Kpi label="Open AR" value={money(aging?.total)} tone={aging && aging.total > 0 ? 'amber' : undefined} />
        <Kpi label="Overdue 30+" value={money(overdue30)} tone={overdue30 > 0 ? 'red' : undefined} />
        <Kpi label="Cash position" value={money(overview?.cashTotal)} />
        <Kpi label="Clients linked" value={`${linked}/${accounts.length}`} />
      </div>

      {/* pipeline */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {COLS.map((col) => (
          <div key={col} className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground text-center">{col}</div>
            {NODES.filter((n) => n.col === col).map(NodeCard)}
          </div>
        ))}
      </div>
      <div className="grid gap-3 grid-cols-1 lg:grid-cols-3">{EXTRA.map(NodeCard)}</div>

      {/* run stepper + detail */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-medium text-sm">Daily billing run</div>
              <div className="flex gap-2">
                <Button size="sm" onClick={play}><Play className="h-3.5 w-3.5 mr-1" /> Play cycle</Button>
                <Button size="sm" variant="outline" onClick={reset}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset</Button>
              </div>
            </div>
            <div className="space-y-2">
              {STEPS.map((s, idx) => {
                const active = idx === step;
                const done = step >= 0 && idx < step;
                const note = isLive ? s.live : s.safe;
                return (
                  <div key={s.t} className={`flex gap-3 items-start rounded-lg border p-2.5 transition
                    ${active ? 'border-primary ring-1 ring-primary' : 'border-border'} ${active || done ? 'opacity-100' : 'opacity-60'}`}>
                    <div className={`h-6 w-6 rounded-full grid place-items-center text-xs font-bold shrink-0
                      ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>{idx + 1}</div>
                    <div>
                      <div className="text-sm font-medium">{s.t}</div>
                      <div className="text-xs text-muted-foreground">{s.d}</div>
                      {note && <div className={`text-[11px] mt-1 ${isLive ? 'text-success' : 'text-warning'}`}>{note}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <div className="font-medium text-base">{d.title}</div>
            <p className="text-sm text-muted-foreground mt-2">{d.body}</p>
            {d.points.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm list-disc pl-5">
                {d.points.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            )}
            <div className="mt-4 pt-3 border-t text-xs text-muted-foreground flex items-center gap-1">
              Flow <ArrowRight className="h-3 w-3" /> click any component above to inspect it.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'amber' | 'red' }) {
  const color = tone === 'red' ? 'text-destructive' : tone === 'amber' ? 'text-warning' : '';
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
